import { useState, useEffect } from 'react';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import Hooks
import { useGTMEvent } from 'hooks/UseGTM';
import { usePartnerDateFormats } from 'hooks/UsePartnerDateFormats';
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Services
import { DashboardHttpService } from 'services/http';
import { getStartDate, getEndDate } from 'services/dashboard';
// Import UI Components
import { BarChart, DatePickerInput, BusyLoader, Empty, ModuleTitle } from '@geneui/components';
// Import Configs
import { datePickerConfigs } from './config';
// Import Constants
import { GACategoryEvents, l } from 'constants/common';
// Import Helpers
import { customMomentWithoutTimezoneConversion } from 'services/Helpers';

const { GADateFilterCategory } = GACategoryEvents;

interface Props {
    parentDate: string[];
}

const CustomerOnBoardActivityChart: React.FC<Props> = ({ parentDate }) => {
    const { doGetRequest } = useRequest();
    const { t } = useTranslation();
    const { logEvent } = useGTMEvent();
    const { partnerDateFormat } = usePartnerDateFormats();

    const [isLoading, setIsLoading] = useState(false);
    const [customerOnBoardActivityData, setCustomerOnBoardActivityDataData] = useState<
        Nullable<{
            series: { name?: string; data: number[] }[];
            categories: string[];
        }>
    >(null);

    const [date, setDate] = useState(parentDate);

    const { getDashboardOnBoardActivityRequest } = useCancelableRequests(
        { getDashboardOnBoardActivityRequest: DashboardHttpService.getDashboardOnBoardActivity },
        { takeLatest: ['getDashboardOnBoardActivityRequest'] },
    );

    const getDashboardOnBoardActivity = (date) => {
        setIsLoading(true);

        doGetRequest(getDashboardOnBoardActivityRequest.request, {
            queryString: { startDate: date[0], endDate: date[1] },
            successCallback: ({ DashboardOnBoardActivityList, SportEventsSeries }) => {
                interface OnBoardActivityListItem {
                    firstDepositCounts: number[];
                    registrationCounts: number[];
                    secondDepositCounts: number[];
                    thirdDepositCounts: number[];
                    categories: string[];
                }

                const dashboardOnBoardActivityList = DashboardOnBoardActivityList.reduce<OnBoardActivityListItem>(
                    (acc, element) => {
                        acc.firstDepositCounts.push(element.FirstDepositCount);
                        acc.registrationCounts.push(element.RegistrationCount);
                        acc.secondDepositCounts.push(element.SecondDepositCount);
                        acc.thirdDepositCounts.push(element.ThirdDepositCount);
                        acc.categories.push(customMomentWithoutTimezoneConversion(element.Date).format('MMM DD'));
                        return acc;
                    },
                    {
                        firstDepositCounts: [],
                        registrationCounts: [],
                        secondDepositCounts: [],
                        thirdDepositCounts: [],
                        categories: [],
                    },
                );

                setCustomerOnBoardActivityDataData({
                    series: [
                        {
                            name: SportEventsSeries.find((r) => r.field === 'RegistrationCount')?.name,
                            data: dashboardOnBoardActivityList.registrationCounts,
                        },
                        {
                            name: SportEventsSeries.find((r) => r.field === 'FirstDepositCount')?.name,
                            data: dashboardOnBoardActivityList.firstDepositCounts,
                        },
                        {
                            name: SportEventsSeries.find((r) => r.field === 'SecondDepositCount')?.name,
                            data: dashboardOnBoardActivityList.secondDepositCounts,
                        },
                        {
                            name: SportEventsSeries.find((r) => r.field === 'ThirdDepositCount')?.name,
                            data: dashboardOnBoardActivityList.thirdDepositCounts,
                        },
                    ],
                    categories: dashboardOnBoardActivityList.categories,
                });
            },
        }).then(({ IsCanceled }) => {
            // A superseded call must not hide the loader of the call that replaced it
            if (!IsCanceled) {
                setIsLoading(false);
            }
        });
    };

    const getDate = (date) => [getStartDate(date[0]), getEndDate(date[1])];

    const handleDatePickerChange = (e) => {
        const date = getDate(e);

        logEvent(
            GADateFilterCategory.name,
            GADateFilterCategory.events.newCustomersDailyMetrics,
            GADateFilterCategory.labels.newCustomersDailyMetrics,
            `${date[0]} - ${date[1]}`,
        );
        setDate(date);
    };

    useEffect(() => {
        getDashboardOnBoardActivity(date);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date[0], date[1]]);

    useEffect(() => {
        setDate(getDate(parentDate));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parentDate]);

    return (
        <>
            <div className="charts-wrap-inner-head">
                <ModuleTitle title={t(l.DailyMetricsForNewCustomers)} />

                <div className="campaign-metrics-wrap-c-h-d">
                    <DatePickerInput.WithRange
                        onChange={handleDatePickerChange}
                        value={date}
                        label={t(l.TimePeriod)}
                        labelAppearance="swap"
                        min={datePickerConfigs.min}
                        max={datePickerConfigs.max}
                        format={partnerDateFormat}
                    />
                </div>
            </div>
            {isLoading ? (
                <BusyLoader spinnerSize="big" isBusy={true} />
            ) : isEmpty(customerOnBoardActivityData?.categories) ? (
                <Empty title={t(l.NoDataToDisplay)} />
            ) : (
                <div className="charts-wrap-inner-content">
                    <BarChart
                        series={customerOnBoardActivityData?.series}
                        categories={customerOnBoardActivityData?.categories}
                        plotOptions={{
                            column: {
                                borderRadius: 4,
                            },
                        }}
                        tooltip={{
                            pointFormat:
                                '<ul style="color:{series.color}"><li>{series.name}: <span>{point.y}</span></li></ul>',
                        }}
                    />
                </div>
            )}
        </>
    );
};

export default CustomerOnBoardActivityChart;
